import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, LayoutDashboard } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { adminLogin, apiError } from "../lib/api";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { BrandMark } from "../components/Brand";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function AdminLogin() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!username.trim() || !password.trim())
      return toast.error(t("pleaseEnterCredentials"));
    setLoading(true);
    try {
      const auth = { username: username.trim(), password };
      await adminLogin(auth);
      login(auth);
      navigate("/admin");
    } catch (e) {
      toast.error(apiError(e) === "Invalid credentials" ? t("invalidCredentials") : t("invalidCredentials"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#26422A]" data-testid="admin-login-page">
      <div className="lf-grain min-h-screen">
        <header className="mx-auto flex max-w-md items-center justify-between px-5 py-6">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/host/login")}
            className="flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-bold text-[#FFEEBC] hover:bg-white/10"
          >
            <ArrowLeft className="h-4 w-4" /> {t("back")}
          </button>
          <LanguageSwitcher />
        </header>

        <main className="mx-auto flex max-w-md flex-col items-center px-5 pt-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full rounded-3xl bg-[#FFFFFF] p-8 shadow-2xl"
          >
            <div className="mb-6 flex justify-center">
              <BrandMark size="lg" />
            </div>
            <h1 className="text-center font-display text-3xl font-black text-[#26422A]">
              {t("adminLogin")}
            </h1>
            <p className="mt-2 flex items-center justify-center gap-1.5 text-sm font-medium text-[#26422A]">
              <LayoutDashboard className="h-4 w-4" /> {t("adminAccessOnly")}
            </p>

            <div className="mt-8 space-y-4">
              <div className="space-y-2">
                <Label className="font-bold text-[#26422A]">{t("username")}</Label>
                <Input
                  data-testid="admin-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t("enterUsername")}
                  className="h-12 rounded-xl border-[#26422A]/60 bg-white font-semibold"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-bold text-[#26422A]">{t("password")}</Label>
                <Input
                  data-testid="admin-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                  placeholder={t("enterPassword")}
                  className="h-12 rounded-xl border-[#26422A]/60 bg-white font-semibold"
                />
              </div>
            </div>

            <Button
              data-testid="admin-login-btn"
              onClick={handleLogin}
              disabled={loading}
              className="mt-8 h-14 w-full rounded-xl bg-[#EA672D] text-base font-bold text-white hover:bg-[#d0561f]"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t("login")}
            </Button>
            <p className="mt-4 text-center text-xs text-[#26422A]">{t("contactManagement")}</p>
          </motion.div>
        </main>
      </div>
    </div>
  );
}
